import type { EpicProgress } from "@beacon/core/projects"

/*
 * The geometry of a Meter, kept apart from its drawing so it can be checked without a browser. The
 * track is the budget: what has been spent fills it from the left, the projection hatches on from
 * there, and neither runs past the end, so a month over budget reads as a full, amber track.
 */

/** Where a share of the budget lands on the track, as a percentage of its width, 0 to 100. */
export function percentOf(amount: number, budget: number) {
  if (!(budget > 0) || !(amount > 0)) return 0
  return Math.min(100, (amount / budget) * 100)
}

/**
 * A Meter's parts, as percentages of the track: the spent fill, the projected segment that starts
 * where it ends, and whether the budget is used up.
 */
export function meterOf({
  spent,
  projected,
  budget,
}: {
  spent: number
  projected?: number
  budget: number
}) {
  const spentPercent = percentOf(spent, budget)
  const projectedPercent = projected === undefined ? 0 : percentOf(projected, budget)
  return {
    spent: spentPercent,
    projected: Math.max(0, projectedPercent - spentPercent),
    reached: budget > 0 && spent >= budget,
  }
}

/** A set of tickets counted as an Epic's progress counts them: `done` is every closed one. */
export type TicketCounts = Omit<EpicProgress, "complete">

/*
 * A cancelled ticket was never going to be done, so progress leaves it out: the track and the
 * figure are the tickets not cancelled, and only the words name the cancelled.
 */

/**
 * How a set of tickets stands, as the parts of a progress track from the left: Done, then Started,
 * each a percentage of the tickets not cancelled, leaving out any with none. The track left over is
 * the tickets not started.
 */
export function progressPartsOf({ total, done, started, cancelled }: TicketCounts) {
  return (
    [
      { category: "done", count: done - cancelled },
      { category: "started", count: started },
    ] as const
  )
    .filter(({ count }) => count > 0)
    .map((part) => ({ ...part, percent: percentOf(part.count, total - cancelled) }))
}

/** The figure beside a track: how many are done of the tickets not cancelled ("1/6"). */
export const progressFigureOf = ({ total, done, cancelled }: TicketCounts) =>
  `${done - cancelled}/${total - cancelled}`

/** How a set of tickets stands in words, as a bar's tooltip and label say it. */
export function progressInWords(counts: TicketCounts) {
  const { total, done, started, cancelled } = counts
  const live = total - cancelled
  const notStarted = total - done - started
  const said = [
    ...progressPartsOf(counts).map(({ category, count }) => `${count} ${category}`),
    ...(notStarted > 0 ? [`${notStarted} not started`] : []),
  ]
  const lead = live
    ? `${live} ${live === 1 ? "ticket" : "tickets"}: ${said.join(", ")}`
    : "No tickets open or done"
  if (!cancelled) return live ? lead : "No tickets yet"
  return `${lead}, and ${cancelled} cancelled`
}
